import React, { createContext, useContext, useMemo, useState } from "react";

type ComplianceCenterState = {
  activeStandard: string | null;
  activeAssessmentType: string;
  activeOrganization: string | null;
  metricsState: "idle" | "loading" | "ready" | "error";
  recommendationsState: "idle" | "loading" | "ready" | "error";
  governanceState: "idle" | "loading" | "ready" | "error";
};

type ComplianceCenterContextType = ComplianceCenterState & {
  setActiveStandard: (value: string | null) => void;
  setActiveAssessmentType: (value: string) => void;
  setActiveOrganization: (value: string | null) => void;
  setMetricsState: (value: ComplianceCenterState["metricsState"]) => void;
  setRecommendationsState: (value: ComplianceCenterState["recommendationsState"]) => void;
  setGovernanceState: (value: ComplianceCenterState["governanceState"]) => void;
};

const ComplianceCenterContext = createContext<ComplianceCenterContextType | null>(null);

export const ComplianceCenterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeStandard, setActiveStandard] = useState<string | null>(null);
  const [activeAssessmentType, setActiveAssessmentType] = useState<string>("SELF_ASSESSMENT");
  const [activeOrganization, setActiveOrganization] = useState<string | null>(null);
  const [metricsState, setMetricsState] = useState<ComplianceCenterState["metricsState"]>("idle");
  const [recommendationsState, setRecommendationsState] = useState<ComplianceCenterState["recommendationsState"]>("idle");
  const [governanceState, setGovernanceState] = useState<ComplianceCenterState["governanceState"]>("idle");

  const value = useMemo(
    () => ({
      activeStandard,
      activeAssessmentType,
      activeOrganization,
      metricsState,
      recommendationsState,
      governanceState,
      setActiveStandard,
      setActiveAssessmentType,
      setActiveOrganization,
      setMetricsState,
      setRecommendationsState,
      setGovernanceState,
    }),
    [activeStandard, activeAssessmentType, activeOrganization, metricsState, recommendationsState, governanceState],
  );

  return <ComplianceCenterContext.Provider value={value}>{children}</ComplianceCenterContext.Provider>;
};

export const useComplianceCenterContext = (): ComplianceCenterContextType => {
  const context = useContext(ComplianceCenterContext);
  if (!context) {
    throw new Error("useComplianceCenterContext must be used inside ComplianceCenterProvider");
  }
  return context;
};

